import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "ml-auto flex" }
const _hoisted_2 = ["href", "textContent"]

import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { FilterMatchMode } from 'primevue/api';
import { DataTableFilterMeta } from 'primevue/datatable';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useToast } from 'primevue/usetoast';
import { useCaseManagerStudentStore } from '@/store';
import { StudentInterface } from '@/types';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface StudentPropsInterface {
  tableData?: StudentInterface[];
  caseManagerId?: number;
  loading?: boolean;
}

interface StudentTableInterface {
  id: number;
  display_name: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CaseManagerStudentTable',
  props: {
    tableData: {},
    caseManagerId: {},
    loading: { type: Boolean }
  },
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const caseManagerStudentStore = useCaseManagerStudentStore();
const students: Ref<StudentInterface[] | undefined> = toRef(props, 'tableData');
const studentCount: Ref<number> = computed(() => (students.value ? students.value.length : 0));
const studentModalOpen = ref(false);
const addStudentDataLoading = ref(false);
const toast = useToast();
const studentItems: Ref<{ name: string; value: number }[]> = ref([]);
const selectedStudents: Ref<StudentInterface[]> = ref([]);
const filters: Ref<DataTableFilterMeta> = ref({
  global: { value: '', matchMode: FilterMatchMode.CONTAINS },
});
const removeSelectedLabel = computed(() => `Remove (${selectedStudents.value.length})`);
const removingSelected = ref(false);

const createMenuModel = (item: StudentTableInterface) => {
  return [
    {
      label: 'Remove Student',
      command: () => {
        detachStudent([item.id], item.display_name);
      },
      visible: can([PERMISSIONS.CASE_MANAGER_DETACH_STUDENTS]),
    },
  ].filter((item) => item.visible);
};

const attachStudents: (ids: number[]) => Promise<void> = async (ids) => {
  await caseManagerStudentStore.attachStudents({ students: ids, caseManagerId: props.caseManagerId as number });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Added',
    detail: 'Student(s) has been added',
    life: 3000,
  });
};

const detachStudent: (ids: number[], studentName: string) => Promise<void> = async (ids, studentName) => {
  await caseManagerStudentStore.detachStudents({ students: ids, caseManagerId: props.caseManagerId as number });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail: `Student ${studentName} has been removed from Case Manager`,
    life: 3000,
  });
};

const detachSelectedStudents = async () => {
  if (removingSelected.value) return;
  removingSelected.value = true;
  const count = selectedStudents.value.length;
  const ids = selectedStudents.value.map((student) => student.id);

  try {
    await caseManagerStudentStore.detachStudents({ students: ids, caseManagerId: props.caseManagerId as number });
    emit(EMIT.UPDATE);
    toast.add({
      severity: 'success',
      summary: 'Removed',
      detail: `${count} student${count === 1 ? '' : 's'} removed from Case Manager`,
      life: 3000,
    });
  } finally {
    selectedStudents.value = [];
    removingSelected.value = false;
  }
};

const openAddStudents = async () => {
  studentItems.value = [];
  if (can([PERMISSIONS.CASE_MANAGER_STUDENTS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.CASE_MANAGER_ATTACH_STUDENTS])) {
    addStudentDataLoading.value = true;
    studentModalOpen.value = true;
    studentItems.value = await caseManagerStudentStore.getAddStudentsModalData(props.caseManagerId as number);
    addStudentDataLoading.value = false;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add students',
      life: 3000,
    });
  }
};

const onCancelAdd = () => (studentModalOpen.value = false);

const onSaveAdd = (students: { name: string; value: number }[]) => {
  const studentIds = students.map((student) => student.value);
  attachStudents(studentIds);
  studentModalOpen.value = false;
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Column = _resolveComponent("Column")!
  const _component_ActionMenu = _resolveComponent("ActionMenu")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createBlock(_component_Card, null, {
    header: _withCtx(() => [
      _cache[3] || (_cache[3] = _createElementVNode("h2", null, "Students", -1)),
      _createVNode(_unref(Chip), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(studentCount.value), 1)
        ]),
        _: 1
      }),
      _createVNode(_component_IconField, {
        "icon-position": "left",
        class: "ml-3"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_InputIcon, null, {
            default: _withCtx(() => [
              _createVNode(_component_Icon, {
                name: "search",
                class: "text-lg"
              })
            ]),
            _: 1
          }),
          _createVNode(_component_InputText, {
            modelValue: filters.value.global.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.global.value) = $event)),
            class: "p-inputtext-sm",
            placeholder: "Search students"
          }, null, 8, ["modelValue"])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        (_unref(can)([_unref(PERMISSIONS).CASE_MANAGER_ATTACH_STUDENTS]))
          ? (_openBlock(), _createBlock(_component_AppButton, {
              key: 0,
              type: "text",
              size: "small",
              text: "Add Students",
              "icon-left": "add",
              onClick: openAddStudents
            }))
          : _createCommentVNode("", true),
        (_unref(can)([_unref(PERMISSIONS).CASE_MANAGER_DETACH_STUDENTS]) && selectedStudents.value.length)
          ? (_openBlock(), _createBlock(_component_AppButton, {
              key: 1,
              type: "alert",
              text: removeSelectedLabel.value,
              loading: removingSelected.value,
              disabled: removingSelected.value,
              onClick: detachSelectedStudents
            }, null, 8, ["text", "loading", "disabled"]))
          : _createCommentVNode("", true)
      ])
    ]),
    content: _withCtx(() => [
      _createVNode(_component_DataTable, {
        filters: filters.value,
        "onUpdate:filters": _cache[1] || (_cache[1] = ($event: any) => ((filters).value = $event)),
        selection: selectedStudents.value,
        "onUpdate:selection": _cache[2] || (_cache[2] = ($event: any) => ((selectedStudents).value = $event)),
        value: students.value,
        "global-filter-fields": ['display_name'],
        scrollable: true,
        "loading-icon": "logo-spinner",
        loading: __props.loading,
        "data-key": "id",
        "responsive-layout": "scroll",
        "scroll-height": "440px",
        class: _normalizeClass({ 'table-loading': __props.loading })
      }, {
        empty: _withCtx(() => [...(_cache[4] || (_cache[4] = [
          _createTextVNode("Students not assigned", -1)
        ]))]),
        default: _withCtx(() => [
          (studentCount.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (_unref(can)([_unref(PERMISSIONS).CASE_MANAGER_DETACH_STUDENTS]))
                  ? (_openBlock(), _createBlock(_component_Column, {
                      key: 0,
                      "selection-mode": "multiple",
                      "header-style": "justify-content: center; max-width: 3rem;",
                      "body-style": "justify-content: center; max-width: 3rem;"
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_Column, {
                  "body-class": "mw-20rem",
                  "header-class": "mw-20rem",
                  field: "display_name",
                  header: "Student Name",
                  sortable: ""
                }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("a", {
                      class: "font-semibold",
                      href: `/user/${data.id}`,
                      textContent: _toDisplayString(data.display_name)
                    }, null, 8, _hoisted_2)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column),
                _createVNode(_component_Column, {
                  "body-class": "justify-content-center mw-10rem",
                  "header-class": "justify-content-center mw-10rem",
                  field: "id",
                  header: "Actions"
                }, {
                  body: _withCtx(({ data }) => [
                    (createMenuModel(data).length > 0)
                      ? (_openBlock(), _createBlock(_component_ActionMenu, {
                          key: 0,
                          "menu-items": createMenuModel(data),
                          icon: "more_horiz"
                        }, null, 8, ["menu-items"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["filters", "selection", "value", "loading", "class"]),
      (studentModalOpen.value && _unref(can)([_unref(PERMISSIONS).CASE_MANAGER_STUDENTS_AVAILABLE_TO_ADD]))
        ? (_openBlock(), _createBlock(AddPersonModal, {
            key: 0,
            title: "Add Students",
            "button-text": "Add Students",
            placeholder: "Search students",
            loading: addStudentDataLoading.value,
            items: studentItems.value,
            searchable: true,
            onAddItems: onSaveAdd,
            onCancel: onCancelAdd
          }, null, 8, ["loading", "items"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})